/**
 * Resolve a card REFERENCE — a typed shorthand (`MOTE-30`) or a parsed
 * card URL — to the work item it names.
 *
 * Backs the editor's auto-detection, which must CONFIRM a work item
 * exists before it converts anything a user typed or pasted into a chip.
 * Two properties matter more here than in a normal query hook:
 *
 *  - **Negative results are cached** on the shorthand path. A reference
 *    that resolves to nothing is the common case while someone is
 *    mid-typing a number, and the same bogus reference gets retyped
 *    constantly (`MOTE-9`, `MOTE-99`, …). Caching the `null` is what
 *    keeps that from becoming a request per keystroke-pause.
 *  - **The card-embed cache is seeded on success.** The chip's NodeView
 *    reads `['card-embed', cardId]`; filling it here means a detected
 *    chip renders its title and status on first paint instead of
 *    mounting a skeleton and fetching the very payload we already hold.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { ApiError, apiClient } from '@/api/client'
import type { ResolvedWorkItem } from '@/editor/work-item-autodetect'
import {
  CARD_EMBED_QUERY_KEY,
  type CardEmbedPayload,
  fetchCardEmbedById,
} from '@/features/wiki/hooks/use-card-embed'
import type { CardReference } from './card-deep-link'

/** One entry per reference, uppercase-normalized so casing can't split it. */
export const CARD_BY_SHORTHAND_QUERY_KEY = (shorthand: string) =>
  ['card-embed-by-shorthand', shorthand.toUpperCase()] as const

/**
 * Detection's own key for a by-id lookup — deliberately NOT
 * `CARD_EMBED_QUERY_KEY`.
 *
 * Resolution answers `null` for a miss, and `fetchQuery` caches a
 * resolved `null` like any other value (only `undefined` is rejected).
 * Writing that under the key `useCardEmbed` reads would leave that hook
 * with `status: 'success'` and no data, which its own `!query.data`
 * branch reports as `isLoading` — a chip already on screen for the same
 * card would flip to a skeleton that never resolves, for the whole
 * `staleTime`. A rethrown network error under that key is the same trap
 * in the other direction: the hook's unknown-error branch renders
 * "Deleted" on a transient blip.
 *
 * So misses and errors live here, and only a CONFIRMED payload is
 * written to the shared key.
 */
export const CARD_BY_ID_RESOLVE_QUERY_KEY = (cardId: string) =>
  ['card-embed-by-id-resolve', cardId] as const

const RESOLVE_STALE_TIME_MS = 5 * 60 * 1000

/**
 * The route answers 404 for every miss — unknown, deleted, cross-tenant,
 * and not-a-member alike — so `null` is the single "no such reference"
 * answer and carries no information about which of those it was. That is
 * deliberate on the BE side (a shorthand is guessable, so a 403 would be
 * an enumeration oracle); the detection rule needs no more than the
 * boolean anyway.
 *
 * Three statuses collapse to that same `null`:
 *  - **404** — no such reference.
 *  - **400** — the reference didn't satisfy the route's shape. Reachable
 *    without a client bug: an announcement's synthesized `ANN-xxxx` code
 *    only ends in digits by chance, so it usually fails the path regex
 *    rather than 404ing. Same underlying fact, so same answer.
 *  - **409 `card_shorthand_ambiguous`** — two live cards answer to it. We
 *    deliberately DON'T surface this as an error: refusing to convert and
 *    leaving the typed text alone is already the correct outcome, and a
 *    toast fired by a passive typing heuristic would be noise. The
 *    catalogue entry exists for any surface that later resolves a
 *    reference on an explicit user action, where an explanation IS owed.
 */
async function fetchCardByShorthand(shorthand: string): Promise<CardEmbedPayload | null> {
  try {
    const res = await apiClient<{ data: CardEmbedPayload }>(
      `/api/cards/by-shorthand/${encodeURIComponent(shorthand.toUpperCase())}?fields=chip`,
    )
    return res.data
  } catch (error) {
    if (error instanceof ApiError && [400, 404, 409].includes(error.status)) return null
    throw error
  }
}

/**
 * Same "did it resolve" question as above, asked by id — the shape a card
 * URL carries for the kinds whose page route is keyed by one.
 *
 * 403 collapses into the same `null` as 404: the by-id route still
 * distinguishes them (the chip NodeView renders "Restricted" vs
 * "Deleted"), but detection has no use for the difference — a reference
 * the reader can't open must stay the plain link they pasted either way.
 */
async function fetchCardById(reference: {
  cardId: string
  kindHint?: CardEmbedPayload['kind']
}): Promise<CardEmbedPayload | null> {
  try {
    return await fetchCardEmbedById(reference.cardId, reference.kindHint)
  } catch (error) {
    if (error instanceof ApiError && [400, 403, 404].includes(error.status)) return null
    throw error
  }
}

/**
 * Is this payload something we may mint a chip for?
 *
 * Two independent reasons a resolved card can still be a miss, and the
 * first is NOT expressible as "it has no code": the single-card route
 * SYNTHESIZES one for announcements (`ANN-3F2A`, from the id), and its
 * response schema declares `code` as always present — so a null-code
 * check is dead code that lets every announcement through.
 *
 *  1. **Announcements never become chips.** They carry no real shorthand,
 *     and the iOS + Android chat projections render a work-item chip as
 *     exactly that string, so one would arrive on mobile as `ANN-3F2A` —
 *     a code that addresses nothing. Reachable by an ordinary route: a
 *     board URL selects an announcement by its id.
 *  2. **Whatever we mint has to survive the server's validator**, which
 *     requires a digits-only shorthand suffix and a UUID `projectId`.
 *     A synthesized `ANN-3F2A` fails the first unless its four hex
 *     characters happen to all be digits, and a TENANT-WIDE announcement
 *     carries `projectId: ''` and fails the second every time — so the
 *     chip would look perfectly normal and then make the whole document
 *     unsaveable with a `richtext_invalid` the author cannot act on.
 *     Checking the shape here keeps that class of failure impossible
 *     rather than merely unlikely.
 */
const CHIPPABLE_SHORTHAND_RE = /^[a-z][a-z0-9]{1,4}-\d{1,9}$/i
const CHIPPABLE_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isChippable(card: CardEmbedPayload): card is CardEmbedPayload & { code: string } {
  if (card.kind === 'announcement') return false
  if (!card.code || !CHIPPABLE_SHORTHAND_RE.test(card.code)) return false
  return CHIPPABLE_UUID_RE.test(card.projectId)
}

export function useResolveCardReference(): (
  reference: CardReference,
) => Promise<ResolvedWorkItem | null> {
  const queryClient = useQueryClient()

  return useCallback(
    async (reference: CardReference) => {
      const card =
        reference.by === 'shorthand'
          ? await queryClient.fetchQuery({
              queryKey: CARD_BY_SHORTHAND_QUERY_KEY(reference.shorthand),
              queryFn: () => fetchCardByShorthand(reference.shorthand),
              staleTime: RESOLVE_STALE_TIME_MS,
              retry: false,
            })
          : // A chip for this card already on screen holds the payload, so
            // read the shared cache first and spend no request at all.
            // Reading it is safe; WRITING a miss there is not — see
            // `CARD_BY_ID_RESOLVE_QUERY_KEY`.
            (queryClient.getQueryData<CardEmbedPayload>(CARD_EMBED_QUERY_KEY(reference.cardId)) ??
            (await queryClient.fetchQuery({
              queryKey: CARD_BY_ID_RESOLVE_QUERY_KEY(reference.cardId),
              queryFn: () => fetchCardById(reference),
              staleTime: RESOLVE_STALE_TIME_MS,
              retry: false,
            })))

      if (!card) return null
      if (!isChippable(card)) return null

      // Seed the per-card cache the chip's NodeView reads, so the chip it
      // is about to become renders resolved rather than loading. This is
      // the ONLY write to that key from here, and it is reached only for
      // a confirmed, chippable payload — which is what keeps a miss from
      // ever appearing there.
      queryClient.setQueryData(CARD_EMBED_QUERY_KEY(card.id), card)

      return {
        id: card.id,
        projectId: card.projectId,
        kind: card.kind,
        code: card.code,
      } satisfies ResolvedWorkItem
    },
    [queryClient],
  )
}
